/**
 * Gets the locale from the HTML document's lang attribute
 * @returns {string} The locale string, defaults to 'en-EN' if not found
 */
function getLocaleFromHTML() {
  const lang = document.documentElement.lang || 'en-EN';
  return lang.includes('dev') ? 'en-EN' : lang;
}

if (!customElements.get('store-reviews')) {
  /**
   * Custom element that displays store-wide review statistics
   * @customElement store-reviews
   * @extends HTMLElement
   */
  customElements.define(
    'store-reviews',
    class StoreReviews extends HTMLElement {
      /**
       * Creates an instance of StoreReviews
       */
      constructor() {
        super();
        this.sectionID = this.dataset.sectionId;
        this.swiperNavigation = this.querySelector('[data-navigation]');
        this.boundFetchReviews = this.fetchReviews.bind(this);
      }

      /**
       * Lifecycle callback when element is added to the DOM
       */
      connectedCallback() {
        this.fetchReviews();
        document.addEventListener('shopify:section:load', this.boundFetchReviews);
      }

      /**
       * Lifecycle callback when element is removed from the DOM
       */
      disconnectedCallback() {
        document.removeEventListener('shopify:section:load', this.boundFetchReviews);
      }

      /**
       * Fetches and displays store review statistics from Reviews.io API
       * @async
       */
      async fetchReviews() {
        const response = await fetch(
          `https://api.reviews.io/stats/all?store=${this.dataset.storeId}`
        );
        // Parse the JSON response
        const data = await response.json(),
          reviewsAverageEl = this.querySelectorAll('.reviews-average'),
          reviewsStarsEl = this.querySelectorAll('.review-stars .star');

        reviewsAverageEl.forEach((el) => {
          el.textContent = parseFloat(data?.product_review_stats?.average_rating).toFixed(1).replace('.', ',') || 0;
        });

        reviewsStarsEl.forEach((el, index) => {
          if (
            index < parseFloat(Math.ceil(data?.product_review_stats?.average_rating).toFixed(1))
          ) {
            el.classList.add('fill-stars');
          }
        });

        this.querySelector('.reviews-total').textContent = new Intl.NumberFormat(
          getLocaleFromHTML(),
          {
            useGrouping: true,
          }
        ).format(data?.product_review_stats?.total_reviews || 0);
      }
    }
  );
}

if (!customElements.get('reviews-count')) {
  /**
   * Custom element that displays review count statistics
   * @customElement reviews-count
   * @extends HTMLElement
   */
  customElements.define(
    'reviews-count',
    class ReviewsCount extends HTMLElement {
      /**
       * Creates an instance of ReviewsCount
       */
      constructor() {
        super();
        this.storeId = this.dataset.storeId;
        this.totalReviews = this.querySelector('[data-total]');
        this.averageScore = this.querySelector('[data-score]');
        this.boundFetchReviews = this.fetchReviews.bind(this);
      }

      /**
       * Lifecycle callback when element is added to the DOM
       */
      connectedCallback() {
        this.fetchReviews();
        document.addEventListener('shopify:section:load', this.boundFetchReviews);
      }

      /**
       * Lifecycle callback when element is removed from the DOM
       */
      disconnectedCallback() {
        document.removeEventListener('shopify:section:load', this.boundFetchReviews);
      }

      /**
       * Fetches and displays review count statistics from Reviews.io API
       * @async
       */
      async fetchReviews() {
        const response = await fetch(
          `https://api.reviews.io/stats/all?store=${this.dataset.storeId}`
        );
        // Parse the JSON response
        const data = await response.json();

        if (this.totalReviews) {
          this.totalReviews.textContent = new Intl.NumberFormat(getLocaleFromHTML(), {
            useGrouping: true,
          }).format(data?.product_review_stats?.total_reviews || 0);
        }

        if (this.averageScore) {
          this.averageScore.textContent =
            parseFloat(data?.product_review_stats?.average_rating).toFixed(1).replace('.', ',') || 0;
        }
      }
    }
  );
}

if (!customElements.get('product-reviews')) {
  /**
   * Custom element that displays product-specific reviews
   * Can handle both individual product reviews and grouped product reviews
   * @customElement product-reviews
   * @extends HTMLElement
   * @property {string} dataset.storeId - The Reviews.io store ID
   * @property {string} dataset.productSku - The SKU of the current product
   * @property {string} [dataset.groupedSku] - Optional comma-separated list of SKUs to group reviews for
   */
  customElements.define(
    'product-reviews',
    class ProductReviews extends HTMLElement {
      /**
       * Creates an instance of ProductReviews
       */
      constructor() {
        super();
        this.boundFetchReviews = this.fetchReviews.bind(this);
      }

      /**
       * Lifecycle callback when element is added to the DOM
       */
      connectedCallback() {
        this.fetchReviews();
        document.addEventListener('shopify:section:load', this.boundFetchReviews);
      }

      /**
       * Lifecycle callback when element is removed from the DOM
       */
      disconnectedCallback() {
        document.removeEventListener('shopify:section:load', this.boundFetchReviews);
      }

      /**
       * Fetches and displays product reviews from Reviews.io API
       * If the product SKU is part of a grouped SKU list, combines reviews from all products in the group
       * @async
       */
      async fetchReviews() {
        const productSku = this.dataset.productSku;
        const groupedSkus = this.dataset.groupedSku
          ? this.dataset.groupedSku.split(',').map((sku) => sku.trim())
          : [];
        const skusToFetch = groupedSkus.includes(productSku) ? groupedSkus : [productSku];


        const reviewPromises = skusToFetch.map((sku) =>
          fetch(
            `https://api.reviews.io/product/reviews?store=${this.dataset.storeId}&sku=${sku}`
          ).then((response) => response.json())
        );

        // Fetch reviews from Reviews.io API
        try {
          const reviewsData = await Promise.all(reviewPromises);

          let totalScore = 0;
          let totalReviews = 0;

          // Calculate the average score and total reviews
          reviewsData.forEach((data) => {
            if (data?.rating) {
              totalScore += parseFloat(data.rating) * data.count;
              totalReviews += data.count;
            }
          });

          // Calculate the average score
          const averageScore = totalReviews > 0 ? totalScore / totalReviews : 0;

          // Update the review stars
          const starElements = this.querySelectorAll('[data-review-star]');
          starElements.forEach((star, index) => {
            if (index < Math.round(averageScore)) {
              star.classList.add('fill-stars');
            } else {
              star.classList.remove('fill-stars');
            }
          });

          // Update the review score and total reviews elements
          const reviewScore = this.querySelector('[data-review-score]');
          const reviewTotal = this.querySelector('[data-review-total]');
          if (totalReviews > 0) {
            if (reviewScore) {
              reviewScore.textContent = averageScore.toFixed(1).replace('.', ',');
            }
            if (reviewTotal) {
              reviewTotal.textContent = new Intl.NumberFormat(getLocaleFromHTML(), {
                useGrouping: true,
              }).format(totalReviews);
            }

            this.classList.remove('opacity-0');
          }
        } catch (error) {
          console.error('ProductReviews: Error fetching reviews:', error);
        }
      }
    }
  );
}
